import React, { useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import searchIcon from 'search.svg';
import FilterToggle from '../Map/FilterToggle';
import DropdownContainer from '../DropdownContainer';
import CheckboxFilterOptions from '../CheckboxFilterOptions';
import EventDateFilter from './EventDateFilter';
import FilterButton from '../FilterButton';
import MobileDropdownCheckboxSection from '../MobileDropdownCheckboxSection';
import EventFilterDropdown from './EventFilterDropdown';
import { EventMapContext } from '../../context/EventMapContext';
import { graphqlDateFormat } from '../../constants/formats.json';
import useOutsideClick from '../../hooks/useOutsideClick';
import useWindowSize from '../../hooks/useWindowSize';
import WindowQuery from '../../utils/WindowQuery';
import sortByName from '../../utils/sortByName';

const areaOptions = [
  {
    slug: 'fort-walton-beach',
    name: 'Ft. Walton Beach',
    id: '1',
    type: 'area',
  },
  { slug: 'destin', name: 'Destin', id: '2', type: 'area' },
  // { slug: 'sandestin', name: 'Sandestin', id: '3', type: 'area' },
  { slug: 'miramar-beach', name: 'Miramar Beach', id: '5', type: 'area' },
  { slug: '30a', name: '30A', id: '4', type: 'area' },
];

const categoryOptions = [
  { slug: 'live_music', name: 'Live Music', id: '1', type: 'category' },
  { slug: 'concert', name: 'Concert', id: '2', type: 'category' },
  { slug: 'trade_show', name: 'Trade Show', id: '3', type: 'category' },
  { slug: 'grand_opening', name: 'Grand Opening', id: '4', type: 'category' },
  { slug: 'open_house', name: 'Open House', id: '5', type: 'category' },
  { slug: 'sale', name: 'Sale', id: '6', type: 'category' },
  { slug: 'party', name: 'Party', id: '7', type: 'category' },
  { slug: 'pub_crawl', name: 'Pub Crawl', id: '8', type: 'category' },
  { slug: 'church', name: 'Church', id: '9', type: 'category' },
  { slug: 'festival', name: 'Festival', id: '10', type: 'category' },
  { slug: 'ceremony', name: 'Ceremony', id: '11', type: 'category' },
  { slug: 'happy_hour', name: 'Happy Hour', id: '12', type: 'category' },
  { slug: 'exercise', name: 'Exercise', id: '13', type: 'category' },
  { slug: 'luncheon', name: 'Luncheon', id: '14', type: 'category' },
  {
    slug: 'food_and_beverage',
    name: 'Food & Beverage',
    id: '15',
    type: 'category',
  },
  { slug: 'sport', name: 'Sport', id: '16', type: 'category' },
  { slug: 'relaxation', name: 'Relaxation', id: '17', type: 'category' },
  { slug: 'kid_friendly', name: 'Kid Friendly', id: '18', type: 'category' },
  {
    slug: 'live_performance',
    name: 'Live Performance',
    id: '19',
    type: 'category',
  },
  {
    slug: 'class_or_workshop',
    name: 'Class/Workshop',
    id: '20',
    type: 'category',
  },
];

function EventsFilterBar() {
  const updateQuery = new WindowQuery();
  const { width: windowWidth } = useWindowSize();

  const {
    setMapVisible,
    mapVisible,
    setDropdown,
    categories,
    startDate,
    endDate,
    areas,
    titleContains,
    setTitleContains,
    datesVisible,
    categoryVisible,
    areaVisible,
    setCurrentPage,
  } = useContext(EventMapContext);

  const container = useRef(null);
  const categoriesRef = useRef(null);
  const datesRef = useRef(null);
  const areasRef = useRef(null);

  useOutsideClick(categoriesRef, categoryVisible, () => setDropdown(null));
  useOutsideClick(datesRef, datesVisible, () => setDropdown(null));
  useOutsideClick(areasRef, areaVisible, () => setDropdown(null));

  let date;
  if (startDate && endDate) {
    // if the months are the same, then display May 18-25
    if (
      moment(startDate, graphqlDateFormat).isSame(
        moment(endDate, graphqlDateFormat),
        'month'
      )
    ) {
      date = `${moment(startDate, graphqlDateFormat).format(
        'MMM D'
      )} -  ${moment(endDate, graphqlDateFormat).format('D')}`;
    }
    // if the months are the same, then display May 18 - Aug 25
    else {
      date = `${moment(startDate, graphqlDateFormat).format(
        'MMM D'
      )} -  ${moment(endDate, graphqlDateFormat).format('MMM D')}`;
    }
  } else if (startDate) {
    date = moment(startDate, graphqlDateFormat).format('MMM D');
  } else {
    date = 'Dates';
  }

  const isMobile = !(windowWidth > 768);

  return (
    <div className="filter-bar">
      <section className="filters-container _event-filters" ref={container}>
        <div className="filter-button-group" ref={categoriesRef}>
          <FilterButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setDropdown('category');
            }}
            type="button"
            className={`filter-button desktop-filter-button ${
              categories.length > 0 ? 'active btn--dark' : ''
            }`}
          >
            {categories.length > 0
              ? `Categories • ${categories.length}`
              : 'Category'}
          </FilterButton>
          <DropdownContainer
            visible={categoryVisible}
            style={{ left: isMobile ? '-20px' : '0px' }}
          >
            <CheckboxFilterOptions
              type="eventCategories"
              data={categoryOptions.sort(sortByName).map(item => ({
                slug: item.slug,
                title: item.name,
                value: item.id,
                type: item.type,
              }))}
            />
          </DropdownContainer>
        </div>

        <div className="filter-button-group" ref={datesRef}>
          <FilterButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setDropdown('dates');
            }}
            type="button"
            className={`filter-button desktop-filter-button ${
              startDate && endDate ? 'active' : ''
            } ${date !== 'Dates' ? 'btn--dark' : ''}`}
            style={{
              position: 'relative',
              outline: 'none',
            }}
          >
            {date}
          </FilterButton>
          <DropdownContainer
            visible={datesVisible}
            style={{
              width: isMobile ? '100vw' : 'auto',
              left:
                isMobile && categoriesRef.current
                  ? `-${categoriesRef.current.clientWidth + 30}px`
                  : '0',
            }}
          >
            <EventDateFilter visible={datesVisible} />
          </DropdownContainer>
        </div>

        <div className="filter-button-group" ref={areasRef}>
          <FilterButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setDropdown('area');
            }}
            type="button"
            className={`filter-button desktop-filter-button ${
              areas.length > 0 ? 'active btn--dark' : ''
            }`}
          >
            {areas.length > 0 ? `Areas • ${areas.length}` : 'Area'}
          </FilterButton>
          <DropdownContainer
            visible={areaVisible}
            style={{
              left:
                isMobile && categoriesRef.current
                  ? `-${datesRef.current.clientWidth +
                      categoriesRef.current.clientWidth +
                      30 +
                      10}px`
                  : '0',
            }}
          >
            <CheckboxFilterOptions
              type="eventAreas"
              data={areaOptions.map(item => ({
                slug: item.slug,
                title: item.name,
                value: item.id,
                type: item.type,
              }))}
            />
          </DropdownContainer>
        </div>
      </section>

      <section style={{ display: 'flex' }}>
        <FilterToggle
          toggleView={() => {
            updateQuery.add('view', !mapVisible ? 'map' : 'list');
            setMapVisible(!mapVisible);
          }}
          toggleLabel={mapVisible ? 'Hide Map' : 'Show Map'}
          checked={mapVisible}
        />
        <div className="search-container">
          <img
            data-src={searchIcon}
            className="search-icon close lazyload"
            alt=""
            title="Search Icon"
          />
          <input
            aria-label="Search Events"
            className="search-input title_cont"
            placeholder="Search Events"
            value={titleContains}
            onBlur={() => {
              if (titleContains) updateQuery.add('title_cont', titleContains);
              else updateQuery.remove('title_cont');
            }}
            onChange={e => {
              setCurrentPage(1);
              setTitleContains(e.target.value);
            }}
          />
        </div>
      </section>
    </div>
  );
}

export default EventsFilterBar;
